import React, { useState, useEffect, useMemo } from 'react';
import Icon from "../icons/Icon";
import { format, startOfMonth, endOfMonth, addMonths, addDays, differenceInCalendarDays, eachDayOfInterval } from 'date-fns';
import { parsePeriodToDate, toYMDString } from '../../utils/periodDate';
import { officialPeriods, isOfficialPeriod, DEFAULT_CUTOFF_START_DAYS } from '../../utils/payCutoffs';
import { holidayService } from '../../services/holidayService';
import PwPopover from '../PwPopover';

const PeriodSelection = ({
  onPeriodConfirm,
  companySettings,
  companyId,
  initialStartDate,
  initialEndDate,
  cutoffStartDays,
  lastCompletedPeriodEnd,
  readOnly = false
}) => {
  const [startDate, setStartDate] = useState(
    initialStartDate ? format(new Date(initialStartDate), 'yyyy-MM-dd') : ''
  );
  const [endDate, setEndDate] = useState(
    initialEndDate ? format(new Date(initialEndDate), 'yyyy-MM-dd') : ''
  );
  const [calendarDays, setCalendarDays] = useState(0);
  const [errors, setErrors] = useState({});
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  // Holidays falling within the selected period (PH regular + special)
  const [holidays, setHolidays] = useState([]);
  const [holidaysStatus, setHolidaysStatus] = useState('idle'); // idle | loading | done | error

  // C8 quick-pick chips: the official tiled pay periods (previous month +
  // current), from settings/payroll.cutoffStartDays.
  const cutoffs = useMemo(
    () =>
      Array.isArray(cutoffStartDays) && cutoffStartDays.length === 2
        ? cutoffStartDays
        : DEFAULT_CUTOFF_START_DAYS,
    [cutoffStartDays]
  );
  const cutoffChips = useMemo(() => {
    const chips = officialPeriods(cutoffs, toYMDString(new Date()), {
      monthsBack: 1,
      monthsForward: 0,
    });
    // C9 forward-only: after a cutoff change, days between the last completed
    // run and the first tiled period surface as a one-off bridge suggestion.
    if (lastCompletedPeriodEnd && chips.length > 0) {
      const bridgeStart = addDays(parsePeriodToDate(lastCompletedPeriodEnd), 1);
      const gapDays = differenceInCalendarDays(
        parsePeriodToDate(chips[0].start),
        bridgeStart
      );
      if (gapDays >= 1 && gapDays <= 31) {
        chips.unshift({
          start: toYMDString(bridgeStart),
          end: toYMDString(addDays(parsePeriodToDate(chips[0].start), -1)),
          isCurrent: false,
          isBridge: true,
        });
      }
    }
    return chips;
  }, [cutoffs, lastCompletedPeriodEnd]);

  // Initialize date restrictions — previous month start .. current month end,
  // WIDENED to admit the tiled cutoff periods (C8: a cross-month current
  // period may end in next month; a bridge chip may start earlier).
  useEffect(() => {
    const today = new Date();
    let lo = startOfMonth(addMonths(today, -1));
    let hi = endOfMonth(today);
    cutoffChips.forEach((p) => {
      const s = parsePeriodToDate(p.start);
      const e = parsePeriodToDate(p.end);
      if (s < lo) lo = s;
      if (e > hi) hi = e;
    });

    // Format for HTML date input (YYYY-MM-DD)
    setMinDate(format(lo, 'yyyy-MM-dd'));
    setMaxDate(format(hi, 'yyyy-MM-dd'));
  }, [cutoffChips]);

  // Populate from props when the period loads AFTER mount (e.g. a draft whose
  // periodStart arrives async). Guarded with `!state` so it only fills blanks
  // and never clobbers in-progress user edits; no-op for fresh runs (props null).
  useEffect(() => {
    if (initialStartDate && !startDate) setStartDate(format(new Date(initialStartDate), 'yyyy-MM-dd'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStartDate]);
  useEffect(() => {
    if (initialEndDate && !endDate) setEndDate(format(new Date(initialEndDate), 'yyyy-MM-dd'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEndDate]);

  // Calculate calendar days when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = differenceInCalendarDays(end, start) + 1;
      setCalendarDays(days);

      // Clear calendar days error if dates are valid
      if (days > 0) {
        setErrors(prev => ({ ...prev, calendarDays: null }));
      }
    } else {
      setCalendarDays(0);
    }
  }, [startDate, endDate]);

  // Fetch holidays in the selected period (presentation only — informational).
  // Follows the canonical date pattern (periodDate.js: local Y/M/D, never
  // toISOString) and filters getHolidaysByYear by 'YYYY-MM-DD' string, sidestepping
  // holidayService.getHolidaysInRange's toISOString (which would drift a day in PH/AU TZ).
  useEffect(() => {
    if (!companyId || !startDate || !endDate || calendarDays <= 0) {
      setHolidays([]);
      setHolidaysStatus('idle');
      return undefined;
    }

    let cancelled = false;
    setHolidaysStatus('loading');

    (async () => {
      try {
        const startD = parsePeriodToDate(startDate);
        const endD = parsePeriodToDate(endDate);
        const startYMD = toYMDString(startD);
        const endYMD = toYMDString(endD);

        const all = [];
        for (let y = startD.getFullYear(); y <= endD.getFullYear(); y++) {
          // eslint-disable-next-line no-await-in-loop
          const yearHolidays = await holidayService.getHolidaysByYear(companyId, y);
          all.push(...yearHolidays);
        }

        const inRange = all
          .filter(h => h.date >= startYMD && h.date <= endYMD)
          .sort((a, b) => a.date.localeCompare(b.date));

        if (!cancelled) {
          setHolidays(inRange);
          setHolidaysStatus('done');
        }
      } catch (e) {
        if (!cancelled) {
          setHolidays([]);
          setHolidaysStatus('error');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, startDate, endDate, calendarDays]);

  // Validate start date
  const validateStartDate = (value) => {
    const newErrors = { ...errors };

    if (!value) {
      newErrors.startDate = 'Start Date is required';
    } else {
      const selectedDate = new Date(value);
      const min = new Date(minDate);
      const max = new Date(maxDate);

      // Check if date is within the allowed payroll window (prev/current month,
      // widened to the tiled cutoff periods — C8)
      if (selectedDate < min || selectedDate > max) {
        newErrors.startDate = 'Start Date is outside the allowed payroll window';
      } else {
        delete newErrors.startDate;

        // If end date exists, check if start is before end
        if (endDate && new Date(value) > new Date(endDate)) {
          newErrors.endDate = 'End Date cannot be before Start Date';
        } else {
          delete newErrors.endDate;
        }
      }
    }

    setErrors(newErrors);
  };

  // Validate end date
  const validateEndDate = (value) => {
    const newErrors = { ...errors };

    if (!value) {
      newErrors.endDate = 'End Date is required';
    } else {
      const selectedDate = new Date(value);
      const min = new Date(minDate);
      const max = new Date(maxDate);

      // Check if date is within the allowed payroll window (prev/current month,
      // widened to the tiled cutoff periods — C8)
      if (selectedDate < min || selectedDate > max) {
        newErrors.endDate = 'End Date is outside the allowed payroll window';
      } else if (startDate && new Date(value) < new Date(startDate)) {
        newErrors.endDate = 'End Date cannot be before Start Date';
      } else {
        delete newErrors.endDate;
      }
    }

    setErrors(newErrors);
  };

  // C8: a chip prefills the date inputs (dates stay fully editable).
  const handleChipSelect = (p) => {
    setStartDate(p.start);
    setEndDate(p.end);
    setErrors(prev => {
      const next = { ...prev };
      delete next.startDate;
      delete next.endDate;
      return next;
    });
  };

  // Handle start date change
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDate(value);
    validateStartDate(value);
  };

  // Handle end date change
  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    validateEndDate(value);
  };

  // Handle form submission
  const handleContinue = () => {
    // Validate all fields
    validateStartDate(startDate);
    validateEndDate(endDate);

    // Check if there are any errors
    const hasErrors = !startDate || !endDate ||
                     errors.startDate || errors.endDate;

    if (hasErrors) {
      return;
    }

    // Call parent callback with period data
    onPeriodConfirm({
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      calendarDays: calendarDays
    });
  };

  // --- Derived display values (presentation only; no logic change) ---
  // parsePeriodToDate (periodDate.js) parses to LOCAL midnight, matching how the
  // app stores/reads period dates — the formatted preview never drifts a day.
  const startD = startDate ? parsePeriodToDate(startDate) : null;
  const endD = endDate ? parsePeriodToDate(endDate) : null;
  const periodSelected = !!(startD && endD && calendarDays > 0);
  const periodLabel = startD && endD
    ? `${format(startD, 'MMM d, yyyy')} – ${format(endD, 'MMM d, yyyy')}`
    : 'No period selected';
  const crossMonth = periodSelected && (
    startD.getMonth() !== endD.getMonth() || startD.getFullYear() !== endD.getFullYear()
  );
  // C8: exact match against a tiled official period gates monthly employees.
  const officialMatch = periodSelected && isOfficialPeriod(cutoffs, startDate, endDate);

  // Calendar facts (schedule-agnostic; accurate across month lengths + leap years)
  const dayList = periodSelected ? eachDayOfInterval({ start: startD, end: endD }) : [];
  const weekdays = dayList.filter(d => d.getDay() >= 1 && d.getDay() <= 5).length;
  const weekends = dayList.length - weekdays;

  const regularCount = holidays.filter(h => h.type === 'regular').length;
  const specialCount = holidays.length - regularCount;
  const holidayValue = holidaysStatus === 'loading' ? '…' : holidaysStatus === 'done' ? holidays.length : '–';

  const dateRules = (
    <div className="ps-rules-pop">
      <div className="ps-rules-pop__head">Date rules</div>
      <ul className="ps-rules-pop__list">
        <li>Only dates within the allowed payroll window (previous month up to the current pay period's end)</li>
        <li>End date on or after the start date</li>
        <li>All fields are required to continue</li>
      </ul>
    </div>
  );

  return (
    <div className="period-selection-container-v2">
      {/* pw-header: step title + live period badge */}
      <div className="pw-header">
        <h2 className="pw-header__title">Select Payroll Period</h2>
        <span className={`pw-header__badge ${periodSelected ? '' : 'pw-header__badge--empty'}`}>
          {periodLabel}
        </span>
      </div>

      <div className="ps-layout">
        {/* Left: Define Period (inputs) */}
        <div className="ps-card">
          <div className="ps-card__title">
            <span><Icon name="calendar" size={16} /></span>
            <span>Define Period Dates</span>
            <PwPopover ariaLabel="Date rules" trigger={<span className="ps-info">ⓘ</span>}>
              {dateRules}
            </PwPopover>
          </div>

          {/* C8: quick-pick chips prefill the editable date inputs */}
          {!readOnly && cutoffChips.length > 0 && (
            <div className="ps-cutoff-chips">
              <div className="ps-cutoff-chips__label">
                Pay periods (from your cutoffs)
              </div>
              <div className="ps-cutoff-chips__row">
                {cutoffChips.map((p) => {
                  const active = startDate === p.start && endDate === p.end;
                  return (
                    <button
                      key={p.start}
                      type="button"
                      className={`ps-cutoff-chip${active ? ' is-active' : ''}${p.isCurrent ? ' is-current' : ''}`}
                      onClick={() => handleChipSelect(p)}
                    >
                      {format(parsePeriodToDate(p.start), 'MMM d')} – {format(parsePeriodToDate(p.end), 'MMM d')}
                      {p.isCurrent && <span className="ps-cutoff-chip__tag">current</span>}
                      {p.isBridge && <span className="ps-cutoff-chip__tag">bridge</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="ps-date-row">
            <div className="ps-field">
              <label className="period-date-label">
                <span>Start Date</span>
                <span className="period-required">*</span>
              </label>
              <div className="period-date-wrapper">
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  onBlur={handleStartDateChange}
                  min={minDate}
                  max={maxDate}
                  className={`period-date-input ${errors.startDate ? 'error' : ''}`}
                  disabled={readOnly}
                />
                <span className="period-calendar-icon"><Icon name="calendar" size={16} /></span>
              </div>
              {errors.startDate && (
                <div className="period-input-error">{errors.startDate}</div>
              )}
            </div>

            <div className="ps-date-sep">→</div>

            <div className="ps-field">
              <label className="period-date-label">
                <span>End Date</span>
                <span className="period-required">*</span>
              </label>
              <div className="period-date-wrapper">
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  onBlur={handleEndDateChange}
                  min={startDate || minDate}
                  max={maxDate}
                  className={`period-date-input ${errors.endDate ? 'error' : ''}`}
                  disabled={readOnly || !startDate}
                />
                <span className="period-calendar-icon"><Icon name="calendar" size={16} /></span>
              </div>
              {errors.endDate && (
                <div className="period-input-error">{errors.endDate}</div>
              )}
            </div>
          </div>

          {/* C8: official-match note — a custom range excludes monthly staff */}
          {periodSelected && !errors.startDate && !errors.endDate && (
            officialMatch ? (
              <div className="ps-cutoff-note ps-cutoff-note--official">
                <Icon name="check" size={14} aria-hidden="true" /> Matches your pay cutoff — monthly-salaried employees are included.
              </div>
            ) : (
              <div className="ps-cutoff-note ps-cutoff-note--custom">
                <Icon name="alert-triangle" size={14} aria-hidden="true" /> Custom period — monthly-salaried employees will be excluded
                from this run. Daily employees are unaffected.
              </div>
            )
          )}

        </div>

        {/* Right: live period preview */}
        <div className="ps-card ps-card--preview">
          <div className="ps-card__title">
            <span><Icon name="bar-chart-3" size={16} /></span>
            <span>Period Preview</span>
          </div>

          {periodSelected ? (
            <div className="ps-preview">
              <div className="ps-preview__range">
                <span>{periodLabel}</span>
                {crossMonth && <span className="ps-preview__chip">Spans 2 months</span>}
              </div>

              <div className="ps-preview__stats">
                <div className="ps-stat">
                  <div className="ps-stat__value">{calendarDays}</div>
                  <div className="ps-stat__label">Calendar Days</div>
                </div>
                <div className="ps-stat">
                  <div className="ps-stat__value">{weekdays}</div>
                  <div className="ps-stat__label">Weekdays</div>
                </div>
                <div className="ps-stat">
                  <div className="ps-stat__value">{weekends}</div>
                  <div className="ps-stat__label">Weekends</div>
                </div>
                <div className="ps-stat">
                  <PwPopover
                    ariaLabel="Holidays in this period"
                    align="end"
                    trigger={
                      <span className="ps-stat__value ps-stat__value--link">
                        {holidayValue}
                        <span className="ps-stat__caret">ⓘ</span>
                      </span>
                    }
                  >
                    <div className="ps-holiday-pop">
                      <div className="ps-holiday-pop__head">
                        {holidaysStatus === 'done'
                          ? `${holidays.length} holiday${holidays.length === 1 ? '' : 's'} · ${regularCount} regular · ${specialCount} special`
                          : 'Holidays in this period'}
                      </div>
                      {holidaysStatus === 'loading' ? (
                        <div className="ps-holiday-pop__empty">Loading…</div>
                      ) : holidaysStatus === 'error' ? (
                        <div className="ps-holiday-pop__empty">Couldn’t load holidays.</div>
                      ) : holidays.length === 0 ? (
                        <div className="ps-holiday-pop__empty">No holidays in this period.</div>
                      ) : (
                        <div className="ps-holiday-pop__list">
                          {holidays.map(h => (
                            <div key={h.id || h.date} className="ps-holiday-row">
                              <span className="ps-holiday-row__date">{format(parsePeriodToDate(h.date), 'MMM d')}</span>
                              <span className="ps-holiday-row__name">{h.name}</span>
                              <span className={`ps-holiday-row__type ps-holiday-row__type--${h.type === 'regular' ? 'regular' : 'special'}`}>
                                {h.type === 'regular' ? 'Regular' : 'Special'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </PwPopover>
                  <div className="ps-stat__label">Holidays</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ps-preview__empty">
              <div className="ps-preview__empty-icon"><Icon name="calendar" size={32} /></div>
              <div>Pick a start and end date to preview the payroll period.</div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="period-navigation-v2">
        {readOnly && (
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <span style={{
              padding: '8px 16px',
              background: 'var(--ui-warning-tint)',
              color: 'var(--ui-warning-text)',
              borderRadius: '8px',
              fontSize: '13px',
            }}>
              Period is locked for this draft. Click Next to continue.
            </span>
          </div>
        )}
        <button
          type="button"
          className="period-btn-primary"
          onClick={handleContinue}
          disabled={
            !startDate ||
            !endDate ||
            !!errors.startDate ||
            !!errors.endDate
          }
        >
          <span>Next: Select Employees</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
};

export default PeriodSelection;
